import { AdvancedFiltersContextProvider, GeneralFiltersView } from '@autone/ui';
import { type PropsWithChildren, useMemo } from 'react';

import {
  useAssortmentAdvancedFilters,
  useAssortmentGeneralFilters,
} from '../../../hooks';
import { useGetAvailableFilterStates } from '../../../hooks/use-get-available-filter-states';
import {
  type TableNamesWithAdvancedFilters,
  type TableNamesWithGeneralFilters,
} from '../../../storage/session-storage';
import { type TableNamesWithAnyFilters } from '../types';

import { ASSORTMENT_ADVANCED_FILTERS_CONFIG_MAP } from './advanced-filters-config';
import { type FilterView } from './FiltersPopoverContent.types';
import { ASSORTMENT_GENERAL_FILTERS_CONFIG_MAP } from './general-filters-config';

interface FiltersPopoverGeneralFiltersContextProps extends PropsWithChildren {
  tableName: TableNamesWithGeneralFilters;
}

const FiltersPopoverGeneralFiltersContext = ({
  tableName,
  children,
}: FiltersPopoverGeneralFiltersContextProps) => {
  const {
    translatedGeneralFilterOptions,
    selectedGeneralFilters,
    setSelectedGeneralFilters,
  } = useAssortmentGeneralFilters(tableName);

  const generalFiltersConfig = useMemo(() => {
    const getTableGeneralFilterGroups =
      ASSORTMENT_GENERAL_FILTERS_CONFIG_MAP[tableName];
    return getTableGeneralFilterGroups(translatedGeneralFilterOptions);
  }, [tableName, translatedGeneralFilterOptions]);

  return (
    <GeneralFiltersView
      filterGroups={generalFiltersConfig}
      selectedFilters={selectedGeneralFilters}
      onSelectedFiltersChange={setSelectedGeneralFilters}
    >
      {children}
    </GeneralFiltersView>
  );
};

interface FiltersPopoverAdvancedFilterContextProps extends PropsWithChildren {
  tableName: TableNamesWithAdvancedFilters;
}

const FiltersPopoverAdvancedFiltersContext = ({
  tableName,
  children,
}: FiltersPopoverAdvancedFilterContextProps) => {
  const {
    translatedAdvancedFilterOptions,
    selectedAdvancedFilters,
    setSelectedAdvancedFilters,
  } = useAssortmentAdvancedFilters(tableName);

  const advancedFiltersConfig = useMemo(() => {
    const getAdvancedFilterConfig =
      ASSORTMENT_ADVANCED_FILTERS_CONFIG_MAP[tableName];
    return getAdvancedFilterConfig(translatedAdvancedFilterOptions);
  }, [tableName, translatedAdvancedFilterOptions]);

  return (
    <AdvancedFiltersContextProvider.V2
      advancedFiltersConfig={advancedFiltersConfig}
      defaultAdvancedFilters={selectedAdvancedFilters ?? undefined}
      onAdvancedFiltersChange={setSelectedAdvancedFilters}
    >
      {children}
    </AdvancedFiltersContextProvider.V2>
  );
};

interface FiltersPopoverContextProps extends PropsWithChildren {
  filterView: FilterView;
  tableName: TableNamesWithAnyFilters;
}

export const FiltersPopoverContext = ({
  filterView,
  tableName,
  children,
}: FiltersPopoverContextProps) => {
  const availableFiltersState = useGetAvailableFilterStates(tableName);

  if (
    filterView === 'general' &&
    availableFiltersState.find((f) => f === 'general')
  ) {
    return (
      <FiltersPopoverGeneralFiltersContext
        tableName={tableName as TableNamesWithGeneralFilters}
      >
        {children}
      </FiltersPopoverGeneralFiltersContext>
    );
  } else if (
    filterView === 'advanced' &&
    availableFiltersState.find((f) => f === 'advanced')
  ) {
    return (
      <FiltersPopoverAdvancedFiltersContext
        tableName={tableName as TableNamesWithAdvancedFilters}
      >
        {children}
      </FiltersPopoverAdvancedFiltersContext>
    );
  }
};
